<script setup>
import { computed, ref } from 'vue';
import Phone from '@vuejs/components/Phone.vue';
import { useAgreements } from '@vuejs/composables/useAgreements';

const PREORDER_ENDPOINT = '/api/order/charity/preorder';
const AGREEMENT_SOURCE = 'share-your-love-newsletter';
const ACTION_CONTACT_LABEL = 'Wyrażam zgodę na kontakt ze strony Savicki w sprawie kolejnych akcji programu Share Your Love.';

const { agreements, agreementLabels, agreementExtend } = useAgreements({
    source: AGREEMENT_SOURCE,
});

const data = (() => {
    const el = document.getElementById('philantrophy-data');
    return el ? JSON.parse(el.textContent) : {};
})();
const newsletter = data.newsletter ?? {};

const formData = ref({
    firstname: '',
    email: '',
    phone: '',
    agreement: {
        action_contact: false,
        savicki_marketing: false,
    },
});

const errors = ref({
    firstname: null,
    email: null,
    phone: null,
    action_contact: null,
});

const isSubmitting = ref(false);
const submitState = ref('idle'); // idle | success | already | error

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+\d][\d\s\-()]{6,}$/;

const validate = () => {
    const next = { firstname: null, email: null, phone: null, action_contact: null };

    const fn = formData.value.firstname.trim();
    if (!fn) {
        next.firstname = 'Podaj imię.';
    } else if (fn.length < 2) {
        next.firstname = 'Imię powinno mieć co najmniej 2 znaki.';
    }

    const email = formData.value.email.trim();
    if (!email) {
        next.email = 'Podaj adres e-mail.';
    } else if (!EMAIL_REGEX.test(email)) {
        next.email = 'Wpisz poprawny adres e-mail.';
    }

    const phone = formData.value.phone.trim();
    if (!phone) {
        next.phone = 'Podaj numer telefonu.';
    } else if (!PHONE_REGEX.test(phone)) {
        next.phone = 'Wpisz poprawny numer telefonu.';
    }

    if (!formData.value.agreement.action_contact) {
        next.action_contact = 'Zaakceptuj wymaganą zgodę.';
    }

    errors.value = next;
    return !next.firstname && !next.email && !next.phone && !next.action_contact;
};

const handleSubmit = async () => {
    if (isSubmitting.value) return;
    if (!validate()) return;

    isSubmitting.value = true;
    submitState.value = 'idle';

    const payload = {
        name: formData.value.firstname.trim(),
        email: formData.value.email.trim(),
        phone: formData.value.phone.trim(),
        type: 'global.waitlist',
        is_agreement: true,
        agreement: ACTION_CONTACT_LABEL,
        is_marketing_agreement: formData.value.agreement.savicki_marketing,
        marketing_agreement: agreements.marketing.text,
    };

    try {
        const response = await fetch(PREORDER_ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
            },
            body: JSON.stringify(payload),
        });

        const result = await response.json().catch(() => null);

        if (response.status === 422) {
            const emailErrors = result?.errors?.email || [];
            submitState.value = emailErrors.length > 0 ? 'already' : 'error';
            return;
        }

        if (!response.ok || result?.errors || result?.success === false) {
            submitState.value = 'error';
            return;
        }

        submitState.value = 'success';
    } catch (err) {
        submitState.value = 'error';
    } finally {
        isSubmitting.value = false;
    }
};

const isLocked = computed(() => ['success', 'already'].includes(submitState.value));
</script>

<template>
    <section class="philantrophy-newsletter py-9 py-lg-12 text-center">
        <div class="philantrophy-newsletter__inner mx-auto px-4 px-lg-9">
            <div class="philantrophy-label philantrophy-label--accent mb-5">
                {{ newsletter.label }}
            </div>
            <h2 class="philantrophy-newsletter__title mb-4">
                {{ newsletter.title }}
            </h2>
            <p class="philantrophy-newsletter__lead mb-7">
                {{ newsletter.lead }}
            </p>

            <form
                v-if="!isLocked"
                class="philantrophy-newsletter__form mx-auto"
                data-manual="true"
                novalidate
                @submit.prevent="handleSubmit"
            >
                <div class="philantrophy-newsletter__field">
                    <input
                        v-model="formData.firstname"
                        type="text"
                        placeholder="Imię"
                        class="philantrophy-newsletter__input"
                        :class="{ 'is-invalid': errors.firstname }"
                        autocomplete="given-name"
                        name="firstname"
                        @input="errors.firstname = null"
                    />
                </div>
                <div class="philantrophy-newsletter__field">
                    <input
                        v-model="formData.email"
                        type="email"
                        :placeholder="newsletter.placeholder"
                        class="philantrophy-newsletter__input"
                        :class="{ 'is-invalid': errors.email }"
                        autocomplete="email"
                        name="email"
                        @input="errors.email = null"
                    />
                </div>

                <div
                    class="philantrophy-newsletter__field philantrophy-newsletter__field--phone"
                    :class="{ 'is-invalid': errors.phone }"
                    @click="errors.phone = null"
                >
                    <Phone
                        v-model="formData.phone"
                        :errors="errors.phone"
                        :inputOptions="{
                            name: 'phone',
                            autocomplete: 'tel',
                            placeholder: 'Numer telefonu',
                        }"
                    />
                </div>

                <div
                    v-if="errors.firstname || errors.email || errors.phone"
                    class="philantrophy-newsletter__errors"
                >
                    <span v-if="errors.firstname">{{ errors.firstname }}</span>
                    <span v-if="errors.email">{{ errors.email }}</span>
                    <span v-if="errors.phone">{{ errors.phone }}</span>
                </div>

                <label
                    class="philantrophy-newsletter__agreement"
                    :class="{ 'is-invalid': errors.action_contact }"
                >
                    <input
                        v-model="formData.agreement.action_contact"
                        type="checkbox"
                        class="philantrophy-newsletter__agreement-input"
                        @change="errors.action_contact = null"
                    />
                    <span class="philantrophy-newsletter__agreement-box" aria-hidden="true"></span>
                    <span class="philantrophy-newsletter__agreement-text">
                        <span class="philantrophy-newsletter__agreement-required" aria-hidden="true">*</span>
                        {{ ACTION_CONTACT_LABEL }}
                    </span>
                </label>
                <div v-if="errors.action_contact" class="philantrophy-newsletter__errors">
                    <span>{{ errors.action_contact }}</span>
                </div>

                <label class="philantrophy-newsletter__agreement">
                    <input
                        v-model="formData.agreement.savicki_marketing"
                        type="checkbox"
                        class="philantrophy-newsletter__agreement-input"
                    />
                    <span class="philantrophy-newsletter__agreement-box" aria-hidden="true"></span>
                    <span
                        class="philantrophy-newsletter__agreement-text"
                        v-html="agreementLabels.marketing"
                    ></span>
                </label>

                <p
                    v-if="agreementExtend?.marketing"
                    class="philantrophy-newsletter__admin-info"
                    v-html="agreementExtend.marketing"
                ></p>

                <button
                    type="submit"
                    class="philantrophy-newsletter__button philantrophy-newsletter__button--solid"
                    :disabled="isSubmitting"
                >
                    {{ isSubmitting ? 'Zapisywanie…' : newsletter.button }} →
                </button>
            </form>

            <div
                v-if="submitState === 'success'"
                class="philantrophy-newsletter__success"
            >
                Dziękujemy! Damy znać, gdy ruszymy z kolejną akcją programu Share Your Love.
            </div>
            <div
                v-else-if="submitState === 'already'"
                class="philantrophy-newsletter__success"
            >
                Twój adres mamy już zapisany. Damy znać, gdy ruszymy z kolejną akcją.
            </div>
            <div
                v-else-if="submitState === 'error'"
                class="philantrophy-newsletter__error"
            >
                Coś poszło nie tak. Spróbuj ponownie za chwilę.
            </div>
        </div>
    </section>
</template>
